import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import {
  getAutomations,
  createAutomation,
  updateAutomation,
  deleteAutomation,
  regenerateAutomationSecret,
  getAutomationRuns,
  getTicketProjects,
  errorMessage,
} from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import { fmtDateTime } from '../utils/format';
import { Pill, PageHeading, Th, NtIcon, NT_COLORS } from '../components/NotionUI';
import Select from '../components/Select';

const EVENT_TYPES = ['attachment_added', 'comment_added', 'ticket_created', 'status_changed'];
const ACTIONS = ['internal_note', 'reply'];

// Icona e colore di ogni evento.
const EVENT_META = {
  attachment_added: { icon: 'paperclip', color: '#2563eb' },
  comment_added: { icon: 'mail', color: '#7c3aed' },
  ticket_created: { icon: 'ticket', color: '#16a34a' },
  status_changed: { icon: 'status', color: '#d97706' },
};

// Indirizzo del webhook accorciato (host + percorso) per la scheda.
const shortUrl = (url) => {
  try {
    const u = new URL(url);
    return `${u.host}${u.pathname === '/' ? '' : u.pathname}`;
  } catch (e) {
    return url;
  }
};

const emptyForm = {
  name: '',
  event_type: 'attachment_added',
  project_id: '',
  webhook_url: '',
  default_action: 'internal_note',
  is_active: true,
  webhook_secret: '',
  opt_prompt: '',
  opt_model: '',
  opt_max_tokens: '',
  opt_max_image_side: '',
  opt_max_pdf_pages: '',
  opt_min_kb: '',
};

const MODELS = [
  { value: 'claude-haiku-5-5', key: 'optModelHaiku' },
  { value: 'claude-sonnet-5-5', key: 'optModelSonnet' },
  { value: 'claude-opus-5-5', key: 'optModelOpus' },
];

// Segreto casuale per il pulsante "Genera" (32 caratteri esadecimali).
const randomSecret = () => {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

const optionsFromForm = (f) => ({
  prompt: f.opt_prompt,
  model: f.opt_model,
  max_tokens: f.opt_max_tokens,
  max_image_side: f.opt_max_image_side,
  max_pdf_pages: f.opt_max_pdf_pages,
  min_bytes: f.opt_min_kb === '' ? '' : Math.round(Number(f.opt_min_kb) * 1024),
});


function Automations({ user, onLogout }) {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  // Aperta dalla coda (?project=ID): si vedono le automazioni di quella coda (e quelle per tutte) e la nuova nasce già su di essa.
  const queueFilter = searchParams.get('project') || '';
  const [automations, setAutomations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState(null);
  const [runsFor, setRunsFor] = useState(null);
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);

  const fetchAutomations = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getAutomations();
      setAutomations(data.automations || []);
    } catch (err) {
      setError(errorMessage(err, t('automations.loadError')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAutomations();
    getTicketProjects().then((d) => setProjects(d.projects || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCreate = () => {
    setForm({ ...emptyForm, project_id: queueFilter });
    setOptionsOpen(false);
    setEditId(null);
    setShowForm(true);
    setRevealedSecret(null);
  };

  const startEdit = (a) => {
    setForm({
      name: a.name,
      event_type: a.event_type,
      project_id: a.project_id || '',
      webhook_url: a.webhook_url,
      default_action: a.default_action,
      is_active: a.is_active,
      webhook_secret: '',
      opt_prompt: a.webhook_options?.prompt ?? '',
      opt_model: a.webhook_options?.model ?? '',
      opt_max_tokens: a.webhook_options?.max_tokens ?? '',
      opt_max_image_side: a.webhook_options?.max_image_side ?? '',
      opt_max_pdf_pages: a.webhook_options?.max_pdf_pages ?? '',
      opt_min_kb: a.webhook_options?.min_bytes != null ? Math.round(a.webhook_options.min_bytes / 1024) : '',
    });
    setOptionsOpen(Object.keys(a.webhook_options || {}).length > 0);
    setEditId(a.id);
    setShowForm(true);
    setRevealedSecret(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: form.name,
        event_type: form.event_type,
        project_id: form.project_id || null,
        webhook_url: form.webhook_url,
        default_action: form.default_action,
        is_active: form.is_active,
        webhook_options: optionsFromForm(form),
      };
      if (form.webhook_secret.trim()) payload.webhook_secret = form.webhook_secret.trim();
      if (editId) {
        await updateAutomation(editId, payload);
        // Il segreto scelto non si rilegge più dopo il salvataggio: lo si mostra qui per copiarlo.
        if (payload.webhook_secret) setRevealedSecret({ name: form.name, secret: payload.webhook_secret });
      } else {
        const res = await createAutomation(payload);
        if (res.automation?.webhook_secret) {
          setRevealedSecret({ name: res.automation.name, secret: res.automation.webhook_secret });
        }
      }
      setShowForm(false);
      fetchAutomations();
    } catch (err) {
      setError(errorMessage(err, t('automations.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(t('automations.deleteConfirm', { name: a.name }))) return;
    try {
      await deleteAutomation(a.id);
      if (runsFor?.id === a.id) setRunsFor(null);
      fetchAutomations();
    } catch (err) {
      setError(errorMessage(err, t('automations.deleteError')));
    }
  };

  const handleRegenerateSecret = async (a) => {
    if (!window.confirm(t('automations.regenerateSecretConfirm', { name: a.name }))) return;
    try {
      const res = await regenerateAutomationSecret(a.id);
      setRevealedSecret({ name: a.name, secret: res.webhook_secret });
    } catch (err) {
      setError(errorMessage(err, t('automations.regenerateSecretError')));
    }
  };

  const toggleActive = async (a) => {
    const next = !a.is_active;
    setAutomations((list) => list.map((x) => (x.id === a.id ? { ...x, is_active: next } : x)));
    try {
      await updateAutomation(a.id, {
        name: a.name,
        event_type: a.event_type,
        project_id: a.project_id || null,
        webhook_url: a.webhook_url,
        default_action: a.default_action,
        is_active: next,
      });
    } catch (err) {
      setError(errorMessage(err, t('automations.saveError')));
      fetchAutomations();
    }
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(revealedSecret.secret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 1500);
    } catch (err) {
      setError(t('automations.copyError'));
    }
  };

  const handleShowRuns = async (a) => {
    setRunsFor({ id: a.id, name: a.name });
    setRunsLoading(true);
    try {
      const res = await getAutomationRuns(a.id);
      setRuns(res.runs || []);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setRunsLoading(false);
    }
  };

  const visible = queueFilter ? automations.filter((x) => !x.project_id || String(x.project_id) === queueFilter) : automations;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="nt-page">
      <PageHeading icon="zap" title={t('automations.title')} subtitle={t('automations.subtitle')} />

      {queueFilter && !showForm && (
        <div className="au-queue-filter">
          <span>{t('automations.filteredQueue', { name: (projects.find((q) => String(q.id) === queueFilter) || {}).name || '…' })}</span>
          <button type="button" className="nt-ghost-btn" onClick={() => setSearchParams({})}>{t('automations.showAll')}</button>
        </div>
      )}

      {!showForm && (
        <div className="tp-toolbar">
          <span className="nt-muted">{loading ? '' : t('automations.count', { count: visible.length })}</span>
          <button className="btn" type="button" onClick={startCreate}>
            {t('automations.newAutomation')}
          </button>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {revealedSecret && (
        <div className="au-secret">
          <div className="au-secret-head">
            <span className="au-secret-icon" aria-hidden="true"><NtIcon name="key" size={18} /></span>
            <div>
              <h3>{t('automations.secretTitle')} — {revealedSecret.name}</h3>
              <p className="tp-hint">{t('automations.secretHint')}</p>
            </div>
          </div>
          <div className="au-secret-code">
            <code>{revealedSecret.secret}</code>
            <button className="btn btn-secondary btn-sm" type="button" onClick={copySecret}>
              {secretCopied ? t('automations.copied') : t('automations.copy')}
            </button>
          </div>
          <button className="btn" type="button" onClick={() => setRevealedSecret(null)}>
            {t('automations.secretClose')}
          </button>
        </div>
      )}

      {!showForm ? (
        <div className="tp-grid-wrap">
          {loading ? (
            <div className="loading">{t('common.loading')}</div>
          ) : visible.length === 0 ? (
            <div className="au-empty">
              <span className="au-empty-icon" aria-hidden="true"><NtIcon name="zap" size={26} /></span>
              <p>{t('automations.noAutomations')}</p>
              <button className="btn" type="button" onClick={startCreate}>{t('automations.newAutomation')}</button>
            </div>
          ) : (
            <div className="tp-cards">
              {visible.map((a) => {
                const ev = EVENT_META[a.event_type] || EVENT_META.ticket_created;
                return (
                  <div key={a.id} className={'tp-card au-card' + (a.is_active ? '' : ' au-card-off')} style={{ '--q': ev.color }}>
                    <div className="tp-card-head">
                      <span className="au-event-icon" aria-hidden="true"><NtIcon name={ev.icon} size={16} /></span>
                      <h3 className="tp-card-name">{a.name}</h3>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={a.is_active}
                        className={'au-switch' + (a.is_active ? ' au-switch-on' : '')}
                        onClick={() => toggleActive(a)}
                        title={a.is_active ? t('automations.active') : t('automations.inactive')}
                        aria-label={t('automations.isActiveLabel')}
                      >
                        <span className="au-switch-knob" />
                      </button>
                    </div>
                    <div className="au-flow">
                      <div className="au-flow-row">
                        <span className="au-flow-label">{t('automations.when')}</span>
                        <span className="au-flow-value"><strong>{t(`automations.event_${a.event_type}`)}</strong></span>
                      </div>
                      <div className="au-flow-row">
                        <span className="au-flow-label">{t('automations.project')}</span>
                        <span className="au-flow-value">{a.project_id ? (a.project_name || '—') : <span className="nt-muted">{t('automations.allProjects')}</span>}</span>
                      </div>
                      <div className="au-flow-row">
                        <span className="au-flow-label">{t('automations.webhookLabel')}</span>
                        <span className="au-flow-value au-mono" title={a.webhook_url}>{shortUrl(a.webhook_url)}</span>
                      </div>
                      <div className="au-flow-row">
                        <span className="au-flow-label">{t('automations.resultLabel')}</span>
                        <span className="au-flow-value">{t(`automations.action_${a.default_action}`)}</span>
                      </div>
                    </div>
                    <div className="tp-actions-row">
                      <button className="tp-act" type="button" onClick={() => handleShowRuns(a)}>
                        <NtIcon name="clock" /> {t('automations.runs')}
                      </button>
                      <button className="tp-act" type="button" onClick={() => handleRegenerateSecret(a)} title={t('automations.regenerateSecret')}>
                        <NtIcon name="key" /> {t('automations.secretShort')}
                      </button>
                      <span className="tp-spacer" />
                      <button className="tp-icon" type="button" onClick={() => startEdit(a)} title={t('common.edit')} aria-label={t('common.edit')}>
                        <NtIcon name="edit" size={16} />
                      </button>
                      <button className="tp-icon tp-icon-danger" type="button" onClick={() => handleDelete(a)} title={t('common.delete')} aria-label={t('common.delete')}>
                        <NtIcon name="trash" size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
              <button type="button" className="tp-card tp-card-new" onClick={startCreate}>
                <span className="pj-new-plus" aria-hidden="true">+</span>
                {t('automations.newAutomation')}
              </button>
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="tp-form">
          <h2 className="tp-form-title">{editId ? t('automations.editAutomation') : t('automations.newAutomation')}</h2>

          <section className="tp-section">
            <h3>{t('automations.sectionWhen')}</h3>
            <div className="tp-fields">
              <div className="form-group tp-full">
                <label>{t('automations.name')}</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div className="form-group tp-full">
                <label>{t('automations.event')}</label>
                <div className="au-tiles" role="radiogroup" aria-label={t('automations.event')}>
                  {EVENT_TYPES.map((ev) => (
                    <button
                      key={ev}
                      type="button"
                      role="radio"
                      aria-checked={form.event_type === ev}
                      className={'au-tile' + (form.event_type === ev ? ' au-tile-on' : '')}
                      style={{ '--q': EVENT_META[ev].color }}
                      onClick={() => setForm({ ...form, event_type: ev })}
                    >
                      <span className="au-event-icon" aria-hidden="true"><NtIcon name={EVENT_META[ev].icon} size={16} /></span>
                      {t(`automations.event_${ev}`)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="form-group tp-full">
                <label>{t('automations.project')}</label>
                <Select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })}>
                  <option value="">{t('automations.allProjects')}</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.code ? `${p.name} (${p.code})` : p.name}</option>
                  ))}
                </Select>
              </div>
            </div>
          </section>

          <section className="tp-section">
            <h3>{t('automations.sectionDo')}</h3>
            <div className="tp-fields">
              <div className="form-group tp-full">
                <label>{t('automations.webhookUrl')}</label>
                <input
                  type="url"
                  value={form.webhook_url}
                  onChange={(e) => setForm({ ...form, webhook_url: e.target.value })}
                  placeholder="https://..."
                  required
                />
                <p className="tp-hint">{t('automations.webhookUrlHint')}</p>
              </div>
              <div className="form-group tp-full">
                <label>{t('automations.defaultAction')}</label>
                <div className="au-tiles au-tiles-2" role="radiogroup" aria-label={t('automations.defaultAction')}>
                  {ACTIONS.map((act) => (
                    <button
                      key={act}
                      type="button"
                      role="radio"
                      aria-checked={form.default_action === act}
                      className={'au-tile' + (form.default_action === act ? ' au-tile-on' : '')}
                      style={{ '--q': 'var(--primary)' }}
                      onClick={() => setForm({ ...form, default_action: act })}
                    >
                      <span className="au-event-icon" aria-hidden="true"><NtIcon name={act === 'reply' ? 'mail' : 'edit'} size={16} /></span>
                      {t(`automations.action_${act}`)}
                    </button>
                  ))}
                </div>
                <p className="tp-hint">{t('automations.defaultActionHint')}</p>
              </div>
              <div className="form-group tp-full">
                <label>{editId ? t('automations.secretFieldEdit') : t('automations.secretField')}</label>
                <div className="au-secret-input">
                  <input
                    value={form.webhook_secret}
                    onChange={(e) => setForm({ ...form, webhook_secret: e.target.value })}
                    placeholder={editId ? t('automations.secretPlaceholderEdit') : t('automations.secretPlaceholderNew')}
                    minLength={16}
                    maxLength={64}
                    pattern="[!-~]{16,64}"
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <button type="button" className="btn btn-secondary" onClick={() => setForm({ ...form, webhook_secret: randomSecret() })}>
                    {t('automations.secretGenerate')}
                  </button>
                </div>
                <p className="tp-hint">{t('automations.secretFieldHint')}</p>
              </div>
              <details className="form-group tp-full au-options" open={optionsOpen} onToggle={(e) => setOptionsOpen(e.currentTarget.open)}>
                <summary>{t('automations.optionsTitle')}</summary>
                <p className="tp-hint">{t('automations.optionsHint')}</p>
                <div className="tp-fields">
                  <div className="form-group tp-full">
                    <label>{t('automations.optPrompt')}</label>
                    <textarea
                      rows={4}
                      maxLength={4000}
                      value={form.opt_prompt}
                      onChange={(e) => setForm({ ...form, opt_prompt: e.target.value })}
                      placeholder={t('automations.optPromptPlaceholder')}
                    />
                    <p className="tp-hint">{t('automations.optPromptHint')}</p>
                  </div>
                  <div className="form-group tp-full">
                    <label>{t('automations.optModel')}</label>
                    <Select value={form.opt_model} onChange={(e) => setForm({ ...form, opt_model: e.target.value })}>
                      <option value="">{t('automations.optModelDefault')}</option>
                      {MODELS.map((m) => (
                        <option key={m.value} value={m.value}>{t(`automations.${m.key}`)}</option>
                      ))}
                    </Select>
                  </div>
                  <div className="form-group">
                    <label>{t('automations.optMaxTokens')}</label>
                    <input type="number" min="100" max="4000" step="1" value={form.opt_max_tokens} onChange={(e) => setForm({ ...form, opt_max_tokens: e.target.value })} placeholder="500" />
                    <p className="tp-hint">{t('automations.optMaxTokensHint')}</p>
                  </div>
                  <div className="form-group">
                    <label>{t('automations.optMaxImageSide')}</label>
                    <input type="number" min="0" max="4000" step="1" value={form.opt_max_image_side} onChange={(e) => setForm({ ...form, opt_max_image_side: e.target.value })} placeholder="1568" />
                    <p className="tp-hint">{t('automations.optMaxImageSideHint')}</p>
                  </div>
                  <div className="form-group">
                    <label>{t('automations.optMaxPdfPages')}</label>
                    <input type="number" min="0" max="100" step="1" value={form.opt_max_pdf_pages} onChange={(e) => setForm({ ...form, opt_max_pdf_pages: e.target.value })} placeholder="20" />
                    <p className="tp-hint">{t('automations.optMaxPdfPagesHint')}</p>
                  </div>
                  <div className="form-group">
                    <label>{t('automations.optMinKb')}</label>
                    <input type="number" min="0" max="10000" step="1" value={form.opt_min_kb} onChange={(e) => setForm({ ...form, opt_min_kb: e.target.value })} placeholder="20" />
                    <p className="tp-hint">{t('automations.optMinKbHint')}</p>
                  </div>
                </div>
              </details>
              <div className="form-group tp-full au-active-row">
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.is_active}
                  className={'au-switch' + (form.is_active ? ' au-switch-on' : '')}
                  onClick={() => setForm({ ...form, is_active: !form.is_active })}
                  aria-label={t('automations.isActiveLabel')}
                >
                  <span className="au-switch-knob" />
                </button>
                <span>{t('automations.isActiveLabel')}</span>
              </div>
            </div>
          </section>

          <div className="tp-savebar">
            <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn" disabled={saving}>
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      )}

      {runsFor && (
        <div className="au-runs">
          <div className="au-runs-head">
            <h3>{t('automations.runsTitle', { name: runsFor.name })}</h3>
            <button className="btn btn-secondary btn-sm" type="button" onClick={() => setRunsFor(null)}>
              {t('automations.runsClose')}
            </button>
          </div>
          {runsLoading ? (
            <div className="loading">{t('common.loading')}</div>
          ) : runs.length === 0 ? (
            <p className="nt-empty">{t('automations.runsEmpty')}</p>
          ) : (
            <div className="nt-table-wrap">
              <table className="nt-table cu-table">
                <thead>
                  <tr>
                    <Th icon="calendar">{t('automations.runsDate')}</Th>
                    <Th icon="status">{t('automations.runsStatus')}</Th>
                    <Th icon="hash">{t('automations.runsHttpStatus')}</Th>
                    <Th icon="text">{t('automations.runsError')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r.id}>
                      <td>{fmtDateTime(r.created_at)}</td>
                      <td>
                        <Pill
                          dot
                          color={r.status === 'completed' ? NT_COLORS.green : r.status === 'notify_failed' ? NT_COLORS.red : NT_COLORS.yellow}
                        >
                          {t(`automations.runStatus_${r.status}`)}
                        </Pill>
                      </td>
                      <td>{r.notify_http_status ?? '—'}</td>
                      <td>{r.notify_error || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      </div>
    </Layout>
  );
}

export default Automations;
